// RoutePreview — /dashboard/routes/:id/preview
//
// Renders the route over its floor plan(s). For each unique floor on
// the route:
//   1. Fetch the parsed layout (gpt-5.5 normalized_json: walls / rooms /
//      doors / corridors / labels).
//   2. Build a NavGraph + run Dijkstra between consecutive location
//      pins to get a polyline that walks rooms / corridors / doors
//      instead of cutting through walls.
//   3. Draw on top of an SVG floor plan: walls (gray), rooms (faint
//      fill), corridors (subtle line), locations (pin + sequence),
//      computed paths (accent polyline with arrowheads).
//
// Multi-floor: each floor renders as its own panel. Cross-floor
// segments aren't path-rendered — there's no floor_transitions schema
// yet so we explicitly note the jump in the panel header.

import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, ServerCrash, MapPin, AlertTriangle,
} from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import EmptyState from "../components/EmptyState.js";
import Skeleton from "../components/Skeleton.js";
import {
  getRoutePreview, friendlyRoutesError,
  type RoutePreviewResponse, type RoutePreviewLocation, type RoutePreviewFloor,
} from "../lib/routesApi.js";
import type { LayoutSubset } from "../lib/navigation/buildGraph.js";
import { astarOrthogonal, rasteriseWalls, type RasterGrid } from "../lib/navigation/gridAstar.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { useEffect, useState } from "react";

type ViewState =
  | { status: "loading" }
  | { status: "ready"; data: RoutePreviewResponse }
  | { status: "error"; message: string };

export default function RoutePreview(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const [view, setView] = useState<ViewState>({ status: "loading" });

  const load = async (): Promise<void> => {
    if (!id) return;
    setView({ status: "loading" });
    try {
      const data = await getRoutePreview(id, tenantParam);
      setView({ status: "ready", data });
    } catch (e) {
      setView({ status: "error", message: friendlyRoutesError(e) });
    }
  };
  useEffect(() => { void load(); }, [id, tenantParam]);

  if (view.status === "loading") {
    return (
      <>
        <Header name={null} />
        <div className="card p-6 space-y-2">
          <Skeleton className="h-9 w-72" />
          <Skeleton className="h-72 w-full" />
        </div>
      </>
    );
  }
  if (view.status === "error") {
    return (
      <>
        <Header name={null} />
        <EmptyState icon={ServerCrash} title="路線預覽載入失敗" description={view.message}
          action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />
      </>
    );
  }

  const { data } = view;
  // Group locations by floor in original sequence order, preserving
  // multi-floor splits so the operator sees the actual sequence.
  const groups = groupByFloor(data.locations);

  return (
    <>
      <Header name={data.route.name} onReload={load} />
      <div className="space-y-5">
        {groups.length === 0 && (
          <EmptyState icon={MapPin} title="此路線沒有檢查點" description="請到路線管理頁加入地點。" />
        )}
        {groups.map((g, gi) => {
          const floor = data.floors.find((f) => f.id === g.floorId) ?? null;
          return (
            <FloorPanel
              key={`${g.floorId}-${gi}`}
              segmentLabel={`段 ${gi + 1} / ${groups.length}`}
              floor={floor}
              locations={g.locations}
            />
          );
        })}
      </div>
    </>
  );
}

function Header({ name, onReload }: { name: string | null; onReload?: () => void | Promise<void> }): React.ReactElement {
  return (
    <PageHeader
      title={name ? `路線預覽 — ${name}` : "路線預覽"}
      description="路線在平面圖上的最近導航路徑（依房間、走道、門結構計算）"
      actions={
        <div className="flex gap-2">
          <Link to="/dashboard/routes" className="btn-secondary text-sm inline-flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> 返回路線
          </Link>
          {onReload && (
            <button className="btn-secondary text-sm" onClick={() => void onReload()}>
              <RefreshCw className="h-4 w-4" /> 重新整理
            </button>
          )}
        </div>
      }
    />
  );
}

interface FloorGroup { floorId: string | null; locations: RoutePreviewLocation[] }
function groupByFloor(locations: RoutePreviewLocation[]): FloorGroup[] {
  const groups: FloorGroup[] = [];
  for (const cp of locations) {
    const last = groups[groups.length - 1];
    if (last && last.floorId === cp.floor_id) {
      last.locations.push(cp);
    } else {
      groups.push({ floorId: cp.floor_id, locations: [cp] });
    }
  }
  return groups;
}

// ── FloorPanel ──────────────────────────────────────────────────────

function FloorPanel({ segmentLabel, floor, locations }: {
  segmentLabel: string;
  floor: RoutePreviewFloor | null;
  locations: RoutePreviewLocation[];
}): React.ReactElement {
  if (!floor) {
    // No floor → none of the segment's locations have an active marker
    // on any floor's published layout. Tell the operator concretely
    // what's missing and how to fix it.
    const codes = locations.map((c) => c.code).join("、");
    return (
      <div className="card p-4">
        <p className="text-sm font-medium text-text-primary mb-1">{segmentLabel} · 尚未在任何樓層平面圖上標記位置</p>
        <p className="text-xs text-text-secondary leading-relaxed">
          這段含 {locations.length} 個地點（{codes}），但都還沒有在任一樓層平面圖上放置標記，因此無法呈現路線。請到對應案場的「樓層檢視」→ 點某層 → 編輯平面圖 → 將這些地點拖到圖上的對應位置後，再回來看本頁。
        </p>
      </div>
    );
  }
  const layout = floor.layout_version?.normalized_json as LayoutSubset | null | undefined;
  const viewbox = floor.layout_version?.viewbox_json as { width?: number; height?: number } | null | undefined;
  const imgSize = layout?.image_size ?? null;
  const width = viewbox?.width ?? imgSize?.width ?? 1000;
  const height = viewbox?.height ?? imgSize?.height ?? 1000;

  const grid: RasterGrid | null = useMemo(() => (layout ? rasteriseWalls(layout) : null), [layout]);

  // Compute polylines between consecutive locations that both have
  // a pin on this floor.
  const segments = useMemo(() => {
    if (!grid) return [];
    const out: { from: RoutePreviewLocation; to: RoutePreviewLocation; polyline: { x: number; y: number }[]; warning: string | null; px: number; meters: number | null }[] = [];
    for (let i = 0; i < locations.length - 1; i++) {
      const a = locations[i];
      const b = locations[i + 1];
      if (a.position_x == null || a.position_y == null) continue;
      if (b.position_x == null || b.position_y == null) continue;
      // Grid A* between the two markers. Output is inherently axis-
      // aligned (4-connected; no diagonal moves) and obeys every wall
      // except those with doors carved out.
      const r = astarOrthogonal(grid, { x: a.position_x, y: a.position_y }, { x: b.position_x, y: b.position_y });
      const polyline = r.polyline.length >= 2
        ? r.polyline
        : [{ x: a.position_x, y: a.position_y }, { x: b.position_x, y: b.position_y }];
      let totalPx = 0;
      for (let k = 0; k < polyline.length - 1; k++) {
        totalPx += Math.hypot(polyline[k + 1].x - polyline[k].x, polyline[k + 1].y - polyline[k].y);
      }
      const meters = layout?.scale_meters_per_pixel
        ? totalPx * layout.scale_meters_per_pixel
        : null;
      out.push({ from: a, to: b, polyline, warning: r.warning, px: totalPx, meters });
    }
    return out;
  }, [grid, locations, layout]);

  const totalMeters = segments.reduce((s, x) => s + (x.meters ?? 0), 0);
  const totalPx = segments.reduce((s, x) => s + x.px, 0);

  return (
    <div className="card p-4 space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-text-primary">
            {segmentLabel} · {floor.display_label ?? `${floor.floor_no}F`} {floor.floor_name}
          </p>
          <p className="text-2xs text-text-muted">
            {locations.length} 個 location
            {layout?.scale_meters_per_pixel ? `，總長 ≈ ${totalMeters.toFixed(1)} m` : `，總長 ≈ ${Math.round(totalPx)} px`}
            {!layout && "（此樓層沒有解析後的平面圖）"}
          </p>
        </div>
      </div>

      <div className="rounded-md border border-border bg-bg-base/50 overflow-hidden">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full h-auto block"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Floor plan layer (walls / rooms / corridors). */}
          {layout && <FloorPlanLayer layout={layout} />}

          {/* Path polylines. */}
          {segments.map((s, i) => (
            <PathLine key={i} polyline={s.polyline} />
          ))}

          {/* Location pins. */}
          {locations.map((cp, i) => (
            <LocationPin key={cp.location_id} cp={cp} index={i} />
          ))}
        </svg>
      </div>

      {segments.some((s) => s.warning) && (
        <ul className="space-y-1 text-2xs">
          {segments.filter((s) => s.warning).map((s, i) => (
            <li key={i} className="inline-flex items-start gap-1 text-warning">
              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
              <span>{s.from.code} → {s.to.code}: {s.warning}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── SVG layers ──────────────────────────────────────────────────────

function FloorPlanLayer({ layout }: { layout: LayoutSubset }): React.ReactElement {
  return (
    <g className="floor-plan-layer">
      {/* Rooms — faint fill so users can see structure without the
          path getting visually buried. */}
      {(layout.rooms ?? []).map((r) => {
        if (r.vertices.length < 3) return null;
        const d = `M ${r.vertices.map((v) => `${v.x} ${v.y}`).join(" L ")} Z`;
        return (
          <path key={`room-${r.id}`} d={d}
            fill="rgba(255,255,255,0.025)"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={1}
          />
        );
      })}
      {/* Walls. */}
      {(layout.walls ?? []).map((w) => (
        <line key={`wall-${w.id}`}
          x1={w.points[0].x} y1={w.points[0].y}
          x2={w.points[1].x} y2={w.points[1].y}
          stroke={w.kind === "exterior" ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.32)"}
          strokeWidth={w.kind === "exterior" ? 2 : 1.4}
          strokeLinecap="square"
        />
      ))}
      {/* Corridors — subtle line so it doesn't compete with the path. */}
      {(layout.corridors ?? []).map((c) => {
        if (c.centerline.length < 2) return null;
        const d = `M ${c.centerline.map((p) => `${p.x} ${p.y}`).join(" L ")}`;
        return (
          <path key={`corridor-${c.id}`} d={d}
            fill="none"
            stroke="rgba(255,255,255,0.10)"
            strokeWidth={Math.max(2, c.width_px ?? 4)}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        );
      })}
      {/* Doors — short tick marks. */}
      {(layout.doors ?? []).map((d) => (
        <circle key={`door-${d.id}`}
          cx={d.hinge.x} cy={d.hinge.y} r={3}
          fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth={1.2}
        />
      ))}
    </g>
  );
}

function PathLine({ polyline }: { polyline: { x: number; y: number }[] }): React.ReactElement | null {
  if (polyline.length < 2) return null;
  const d = `M ${polyline.map((p) => `${p.x} ${p.y}`).join(" L ")}`;
  return (
    <g>
      {/* Soft halo so the path stays legible on busy plans. */}
      <path d={d} fill="none" stroke="rgba(0,212,255,0.20)" strokeWidth={8} strokeLinecap="round" strokeLinejoin="round" />
      <path d={d} fill="none" stroke="#00d4ff" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
    </g>
  );
}

function LocationPin({ cp, index }: { cp: RoutePreviewLocation; index: number }): React.ReactElement | null {
  if (cp.position_x == null || cp.position_y == null) return null;
  const x = cp.position_x;
  const y = cp.position_y;
  return (
    <g>
      <circle cx={x} cy={y} r={11} fill="#00d4ff" opacity={0.18} />
      <circle cx={x} cy={y} r={7} fill="#00d4ff" stroke="#0a0a0c" strokeWidth={1.5} />
      <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={8} fontWeight={600} fill="#0a0a0c">
        {index + 1}
      </text>
      <text x={x} y={y - 14} textAnchor="middle" fontSize={10} fontWeight={600} fill="#fff" stroke="#0a0a0c" strokeWidth={2.5} paintOrder="stroke">
        {cp.code}
      </text>
    </g>
  );
}
